// src/components/NavigationBar.jsx

import React, { useState, useEffect } from 'react';
import { copyText } from '../utils/clipboard';
import { Navbar, Nav, Container, Button, Toast, ToastContainer } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import inviteService from '../services/inviteService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/logo.png';
import { LogOut, User, Users, ThumbsUp, MessageCircle } from 'lucide-react';
import { ROLES } from '../App';

const NavigationBar = () => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, auth } = useAuth();
  const [copiedInvite, setCopiedInvite] = useState(false);

  // Collapse menu on route change (use pathname, not the whole location object,
  // so non-navigation state changes don't spuriously close the menu)
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user role from auth context
  const userRole = auth?.user?.role;

  // The /pm/:token magic-link deck is a focused, standalone matches surface.
  // Strip the general-purpose links (About, Contact, Copy CRNA Invite) there so
  // the header stays minimal — only Provider Dashboard + Logout remain.
  const isMagicDeck = location.pathname.startsWith('/pm/');

  // The /rm/:token recruiter daily-matches deck is a focused, anonymous
  // magic-link surface. Keep the header minimal — only the Employers CTA.
  // Drop Providers / Log in / Join Now so it reads like a direct response,
  // not the full marketing nav.
  const isRecruiterDeck = location.pathname.startsWith('/rm/');

  return (
    <Navbar
      bg="white"
      variant="light"
      expand="md"
      sticky="top"
      className="shadow-sm"
      expanded={expanded}
      onToggle={(next) => setExpanded(next)}
    >
      {/* Full-bleed with px-3/px-md-4 (16px / 24px) so the logo sits at the same
          viewport edge as the LandingNavbar used on the other public pages,
          rather than being indented by a centered, max-width container. w-100
          forces the container to span the row (its default width:100% is lost to
          a global override here), so ms-auto still pushes the links to the edge. */}
      <Container fluid className="w-100 px-3 px-md-4">
        <LinkContainer to="">
          <Navbar.Brand>
            <img
              src={logo}
              width="160"
              height="40"
              decoding="async"
              fetchpriority="high"
              className="d-inline-block align-top"
              alt="RxRooster Logo"
            />
          </Navbar.Brand>
        </LinkContainer>

        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto align-items-end gap-2" style={{ flexWrap: 'wrap', maxWidth: '600px' }}>
            {isAuthenticated ? (
              <>
                {/* Common links for all authenticated users (hidden on the
                    /pm magic-link deck — keep that header minimal) */}
                {!isMagicDeck && (
                  <>
                    <LinkContainer to="about">
                      <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        About
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="contact">
                      <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        Contact
                      </Nav.Link>
                    </LinkContainer>
                  </>
                )}

                {/* Quick copy invite for CRNA (hidden on the /pm magic-link deck) */}
                {userRole === ROLES.CRNA && !isMagicDeck && (
                  <>
                    <Button variant="outline-primary" size="sm" className="me-2" onClick={async () => {
                      try {
                        const data = await inviteService.generateInvite('CRNA_REFERRAL');
                        const success = await copyText(data.url);
                        if (!success) {
                          // As a last resort, show the URL so the user can copy manually
                          window.prompt('Copy this invite link:', data.url);
                        }
                        setCopiedInvite(true);
                      } catch (err) {
                        console.error('Failed to copy invite', err);
                      }
                    }}>
                      Copy CRNA Invite
                    </Button>
                  </>
                )}

                {/* For CRNA users, show provider dashboard */}
                {userRole === ROLES.CRNA && (
                  <LinkContainer to="profile">
                    <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                      <User size={16} className="me-1" />
                      Provider Dashboard
                    </Nav.Link>
                  </LinkContainer>
                )}

                {/* For GROUP users, show group dashboard & swipe/matches */}
                {userRole === ROLES.GROUP && (
                  <>
                    <LinkContainer to="group">
                      <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        <Users size={16} className="me-1" />
                        Group Dashboard
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="group-provider-swipe">
                      <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        <ThumbsUp size={16} className="me-1" />
                        Find Providers
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to={{ pathname: "/company", search: "?view=groupMatches" }}>
                      <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        <MessageCircle size={16} className="me-1" />
                        My Matches
                      </Nav.Link>
                    </LinkContainer>
                  </>
                )}

                {/* For ADMIN users, show admin dashboard */}
                {userRole === ROLES.ADMIN && (
                  <LinkContainer to="admin">
                    <Nav.Link className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                      <User size={16} className="me-1" />
                      Admin Dashboard
                    </Nav.Link>
                  </LinkContainer>
                )}

                {/* Logout button for all authenticated users */}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleLogout}
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <LogOut size={16} className="me-1" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <LinkContainer to="/employers">
                  <Nav.Link>Employers</Nav.Link>
                </LinkContainer>
                {!isRecruiterDeck && (
                  <>
                    <LinkContainer to="/providers">
                      <Nav.Link>Providers</Nav.Link>
                    </LinkContainer>
                    {location.pathname !== '/login' && (
                      <LinkContainer to="/login">
                        <Nav.Link>Log in</Nav.Link>
                      </LinkContainer>
                    )}
                    <LinkContainer to="/register">
                      <Nav.Link>Join Now</Nav.Link>
                    </LinkContainer>
                  </>
                )}
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
      {/* Toast notification */}
      <ToastContainer className="p-3" position="top-center" style={{ zIndex: 1060 }}>
        <Toast bg="success" show={copiedInvite} onClose={() => setCopiedInvite(false)} delay={2000} autohide>
          <Toast.Body className="text-white small">Link copied!</Toast.Body>
        </Toast>
      </ToastContainer>
    </Navbar>
  );
};

export default NavigationBar;
